import React, { useMemo, useEffect, useRef } from 'react';
import {
    Box,
    Button,
    Container,
    Header,
    SpaceBetween,
    StatusIndicator,
    Alert,
    Popover,
    Icon,
} from '@amzn/awsui-components-react-v3';
import { useDispatch } from 'react-redux';
import { ActivityData, ClassroomStatus } from '../../../../interfaces/activity';
import {
    useClassroomSetupStatus,
    SetupStep,
    StatusIndicatorType,
} from './useClassroomSetupStatus';
import useClassroomManagement from '../../Common/useClassroomManagement';
import { InfoLink } from '../../../../../common/components/InfoLink/InfoLink';
import { useHelpPanel } from '../../../../../common/context/HelpPanelContext';
import { CLASSROOM_PANEL_CONTENT } from '../../../../../common/components/HelpPanel/HelpPanelContent';
import useClassroomStatusPolling from '../../Common/useClassroomStatusPolling';
import { ActivityModality } from '../../Common/Common';
import { WEBEX_SITE } from '../../../../constants';
import './ClassroomStatusPanel.css';

// Polling interval in milliseconds (5 seconds)
const VILT_POLLING_INTERVAL = 5000;

// 60 polls × 5s interval = 5 min. P99 creation time is 13s, so anything
// still pending beyond this is almost certainly a data issue.
const VILT_MAX_POLLS = 60;

interface ClassroomStatusPanelProps {
    activity: ActivityData;
}

/**
 * Maps our status types to Cloudscape StatusIndicator types
 */
const mapStatusToIndicator = (
    status: StatusIndicatorType,
):
    | 'success'
    | 'error'
    | 'warning'
    | 'info'
    | 'stopped'
    | 'pending'
    | 'in-progress'
    | 'loading' => {
    return status;
};

/**
 * Renders an individual setup step with status indicator
 */
const SetupStepItem: React.FC<{ step: SetupStep }> = ({ step }) => {
    return (
        <div
            className="classroom-setup-step"
            data-testid={`setup-step-${step.id}`}
        >
            <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                <StatusIndicator type={mapStatusToIndicator(step.status)}>
                    {step.label}
                </StatusIndicator>
                {step.helpText && (
                    <Popover
                        dismissButton={false}
                        position="top"
                        size="small"
                        triggerType="custom"
                        content={
                            <Box color="text-body-secondary">
                                {step.helpText}
                            </Box>
                        }
                    >
                        <span className="classroom-setup-help-icon">
                            <Icon name="status-info" variant="subtle" />
                        </span>
                    </Popover>
                )}
            </SpaceBetween>
            {step.detail && (
                <Box
                    color="text-status-error"
                    fontSize="body-s"
                    padding={{ left: 'l' }}
                >
                    {step.detail}
                </Box>
            )}
        </div>
    );
};

/**
 * ClassroomStatusPanel displays the classroom setup progress as a checklist,
 * replacing the previous button-driven approach with a status-driven display.
 *
 * Features:
 * - Shows checklist of eligibility requirements with real-time status indicators
 * - Displays WebEx meeting creation progress
 * - Shows classroom creation status
 * - Provides retry button for failed states
 * - Shows actionable error messages
 */
export const ClassroomStatusPanel: React.FC<ClassroomStatusPanelProps> = ({
    activity,
}) => {
    const dispatch = useDispatch();
    const {
        steps,
        overallStatus,
        canRetry,
        errorMessage,
        hasClassroom,
        viltMeetingStatus,
        classroomStatus,
        isEligible,
        isWebexLicenseError = false,
        licenseRequiredCoHosts = [],
    } = useClassroomSetupStatus(activity);

    const { handleSubmitEvent, submitting } = useClassroomManagement();
    const {
        actions: { makeHelpPanelHandler },
    } = useHelpPanel();

    // Compute if all prerequisites are met for auto-triggering classroom creation
    const shouldAutoTrigger = useMemo(() => {
        const isVirtual =
            activity.activity_modality === ActivityModality.vILT ||
            activity.activity_modality === ActivityModality.Hybrid;

        const webexReady = !isVirtual || viltMeetingStatus.type === 'completed';
        const allPrerequisitesMet = isEligible && webexReady;

        // Classroom creation not yet triggered - includes null/empty AND the "allowed" statuses
        // COURSE_ALLOWED_FOR_CLASSROOM_CREATION means eligible but not yet started
        // ELIGIBLE_FOR_CLASSROOM_CREATION is another pre-start status
        // READY_FOR_CLASSROOM_CREATION means it's already triggered and in progress - do NOT trigger again
        const classroomNotYetTriggered =
            !classroomStatus ||
            classroomStatus === '' ||
            classroomStatus === null ||
            classroomStatus ===
                ClassroomStatus.COURSE_ALLOWED_FOR_CLASSROOM_CREATION ||
            classroomStatus === ClassroomStatus.ELIGIBLE_FOR_CLASSROOM_CREATION;

        // If already in READY_FOR_CLASSROOM_CREATION, don't trigger - it's already in progress
        const classroomCreationInProgress =
            classroomStatus === ClassroomStatus.READY_FOR_CLASSROOM_CREATION;

        const notInErrorState =
            classroomStatus !== ClassroomStatus.CLASSROOM_UPDATE_FAILED &&
            classroomStatus !== ClassroomStatus.CLASSROOM_PROVISIONING_FAILED;

        const shouldTrigger =
            allPrerequisitesMet &&
            classroomNotYetTriggered &&
            !classroomCreationInProgress &&
            notInErrorState &&
            !hasClassroom;

        return shouldTrigger;
    }, [
        isEligible,
        viltMeetingStatus.type,
        classroomStatus,
        hasClassroom,
        activity.activity_modality,
    ]);

    // Store the submit handler in a ref to avoid effect re-running due to handler changes
    const handleSubmitRef = useRef(handleSubmitEvent);
    handleSubmitRef.current = handleSubmitEvent;

    // Track if we've already triggered classroom creation for this activity
    const triggeredForActivityRef = useRef<string | null>(null);

    // Auto-trigger classroom creation when all prerequisites are met
    useEffect(() => {
        // Only trigger if:
        // 1. All prerequisites are met
        // 2. Not currently submitting
        // 3. We haven't triggered for this specific activity yet
        const activityId = activity?.pk;

        if (
            shouldAutoTrigger &&
            !submitting &&
            activityId &&
            triggeredForActivityRef.current !== activityId
        ) {
            triggeredForActivityRef.current = activityId;
            handleSubmitRef.current();
        }

        // Reset the trigger flag if classroom is ready (allow future re-triggers if needed)
        if (hasClassroom) {
            triggeredForActivityRef.current = null;
        }
    }, [shouldAutoTrigger, submitting, activity?.pk, hasClassroom]);

    // Determine if we should poll for activity updates
    // Poll when:
    // 1. vILT meeting creation is in progress (PROCESSING status)
    // 2. It's a vILT/Hybrid activity that should have a vILT URL but doesn't yet
    // 3. All prerequisites are met but classroom isn't ready yet
    // 4. vILT meeting creation shows COMPLETED but v_ilt_id is missing (stale data)
    const shouldPoll = useMemo(() => {
        const isViltOrHybrid =
            activity.activity_modality === ActivityModality.vILT ||
            activity.activity_modality === ActivityModality.Hybrid;

        // Poll if vILT meeting is being created
        if (viltMeetingStatus.type === 'processing') {
            return true;
        }

        // Poll if v_ilt_meeting_creation_status is COMPLETED but v_ilt_id is missing.
        // This indicates stale/inconsistent Redux data after an activity update where
        // v_ilt_id wasn't included in the form data merge. The backend has completed
        // vILT creation, but the frontend doesn't have the updated v_ilt_id yet.
        if (viltMeetingStatus.type === 'completed' && !activity.v_ilt_id) {
            return true;
        }

        // Poll if vILT/Hybrid activity is Active but missing v_ilt_id.
        // This covers scenarios where vILT creation hasn't started yet.
        //
        // IMPORTANT: Only poll for vILT types that use auto-creation (WebEx integration).
        // For v_ilt_type === 'Other', the user manually enters the v_ilt_id, so don't poll.
        const isActive = activity.activity_status === 'Active';
        const usesAutoCreatedVilt =
            activity.v_ilt_type && activity.v_ilt_type !== 'Other';
        if (
            isViltOrHybrid &&
            isActive &&
            usesAutoCreatedVilt &&
            !activity.v_ilt_id &&
            viltMeetingStatus.type !== 'error'
        ) {
            return true;
        }

        // Poll if overall status is in-progress or eligible (waiting for classroom)
        // and we don't have a classroom yet
        if (
            !hasClassroom &&
            (overallStatus === 'in-progress' || overallStatus === 'eligible')
        ) {
            return true;
        }

        return false;
    }, [
        activity.activity_modality,
        activity.activity_status,
        activity.v_ilt_type,
        activity.v_ilt_id,
        viltMeetingStatus.type,
        hasClassroom,
        overallStatus,
    ]);

    // Use polling hook to refresh activity data when waiting for updates.
    // maxPolls is set to 60 so polling stops after 5 minutes (60 × 5s).
    // Classroom creation completes in under 20s in practice (P99 = 13s),
    // so anything still pending after 5 minutes is almost certainly a data issue.
    const { pollLimitReached } = useClassroomStatusPolling(
        shouldPoll,
        activity,
        dispatch,
        VILT_POLLING_INTERVAL,
        VILT_MAX_POLLS,
    );

    const getHeaderDescription = (): string => {
        switch (overallStatus) {
            case 'completed':
                return 'Classroom is ready for use';
            case 'in-progress':
                return 'Setting up classroom...';
            case 'error':
                return 'There was an issue with classroom setup';
            case 'eligible':
                return 'All requirements met - classroom will be created automatically';
            case 'not-started':
            default:
                return 'Complete the requirements below to enable classroom creation';
        }
    };

    const renderRetryButton = () => {
        if (!canRetry) return null;

        return (
            <Button
                data-testid="retry-classroom-button"
                onClick={handleSubmitEvent}
                loading={submitting}
                iconName="refresh"
            >
                Retry
            </Button>
        );
    };

    const renderHeaderActions = () => {
        return (
            <SpaceBetween direction="horizontal" size="xs">
                {renderRetryButton()}
            </SpaceBetween>
        );
    };

    return (
        <Container
            data-testid="classroom-status-panel"
            header={
                <Header
                    variant="h2"
                    description={getHeaderDescription()}
                    actions={renderHeaderActions()}
                    info={
                        <InfoLink
                            onFollow={makeHelpPanelHandler(
                                CLASSROOM_PANEL_CONTENT.title,
                            )}
                        />
                    }
                >
                    Classroom Setup
                </Header>
            }
        >
            <SpaceBetween size="s">
                {/* Error Alert - only show for actionable errors */}
                {errorMessage && (
                    <Alert
                        type="error"
                        data-testid="classroom-error-alert"
                        action={
                            canRetry ? (
                                <Button
                                    onClick={handleSubmitEvent}
                                    loading={submitting}
                                >
                                    Retry
                                </Button>
                            ) : undefined
                        }
                    >
                        <SpaceBetween size="xs">
                            <Box>{errorMessage}</Box>
                            {isWebexLicenseError &&
                                licenseRequiredCoHosts.length > 0 && (
                                    <Box data-testid="webex-license-cohosts">
                                        <Box fontWeight="bold">
                                            One or more of the people below does
                                            not have a WebEx Meetings license
                                            for {WEBEX_SITE}, so host
                                            permissions were not applied
                                            successfully. For anyone missing a
                                            license, remove the assignment and
                                            re-add them once the license is
                                            applied in their WebEx account:
                                        </Box>
                                        <ul>
                                            {licenseRequiredCoHosts.map(
                                                (coHost) => (
                                                    <li key={coHost.email}>
                                                        {coHost.role}:{' '}
                                                        {coHost.name} (
                                                        {coHost.email})
                                                    </li>
                                                ),
                                            )}
                                        </ul>
                                    </Box>
                                )}
                        </SpaceBetween>
                    </Alert>
                )}

                {/* Setup Steps Checklist - this is the primary status display */}
                <div className="classroom-setup-steps">
                    {steps.map((step) => (
                        <SetupStepItem key={step.id} step={step} />
                    ))}
                </div>

                {/* Additional info based on status - only show one message */}
                {!hasClassroom && shouldPoll && !pollLimitReached && (
                    <Box color="text-body-secondary" fontSize="body-s">
                        <Icon name="status-in-progress" /> This process
                        typically takes 1-2 minutes. The page will update
                        automatically.
                    </Box>
                )}
                {!hasClassroom && pollLimitReached && (
                    <Box color="text-body-secondary" fontSize="body-s">
                        Classroom creation is taking longer than expected.
                        Refresh the page to check the latest status.
                    </Box>
                )}
            </SpaceBetween>
        </Container>
    );
};

export default ClassroomStatusPanel;
